/**
 * Theme Management System
 * 
 * Provides comprehensive theming for the chess application including:
 * - Light/Dark UI modes
 * - Multiple board color schemes
 * - Persistent theme preferences via localStorage
 */

// UI Theme modes
export type UITheme = 'light' | 'dark';

// Board color schemes
export type BoardTheme = 'classic' | 'wood' | 'blue' | 'green' | 'gray';

// Theme configuration interface
export interface ThemeConfig {
  uiTheme: UITheme;
  boardTheme: BoardTheme;
}

// Board color scheme definitions
export interface BoardColors {
  lightSquare: string;
  darkSquare: string;
  lightSquareHighlight: string;
  darkSquareHighlight: string;
}

// Available board color schemes
export const BOARD_THEMES: Record<BoardTheme, BoardColors> = {
  classic: {
    lightSquare: '#f0d9b5',
    darkSquare: '#b58863',
    lightSquareHighlight: '#f7ec74',
    darkSquareHighlight: '#d8c34a',
  },
  wood: {
    lightSquare: '#efd9b2',
    darkSquare: '#8b4513',
    lightSquareHighlight: '#f5e7b2',
    darkSquareHighlight: '#b8860b',
  },
  blue: {
    lightSquare: '#e8f0f8',
    darkSquare: '#5b8db8',
    lightSquareHighlight: '#c5e4f5',
    darkSquareHighlight: '#4a7ba7',
  },
  green: {
    lightSquare: '#e8f5e9',
    darkSquare: '#66a86d',
    lightSquareHighlight: '#c8e6c9',
    darkSquareHighlight: '#4caf50',
  },
  gray: {
    lightSquare: '#e0e0e0',
    darkSquare: '#757575',
    lightSquareHighlight: '#eeeeee',
    darkSquareHighlight: '#9e9e9e',
  },
};

// UI theme CSS custom properties
export const UI_THEME_COLORS = {
  light: {
    '--bg-primary': '#ffffff',
    '--bg-secondary': '#f8f9fa',
    '--bg-tertiary': '#e9ecef',
    '--text-primary': '#212529',
    '--text-secondary': '#6c757d',
    '--border-color': '#dee2e6',
    '--shadow-color': 'rgba(0, 0, 0, 0.1)',
    '--card-bg': '#ffffff',
    '--input-bg': '#ffffff',
    '--button-bg': '#007bff',
    '--button-hover': '#0056b3',
  },
  dark: {
    '--bg-primary': '#1a1a1a',
    '--bg-secondary': '#2d2d2d',
    '--bg-tertiary': '#3a3a3a',
    '--text-primary': '#e0e0e0',
    '--text-secondary': '#a0a0a0',
    '--border-color': '#444444',
    '--shadow-color': 'rgba(0, 0, 0, 0.5)',
    '--card-bg': '#2d2d2d',
    '--input-bg': '#3a3a3a',
    '--button-bg': '#0d6efd',
    '--button-hover': '#0a58ca',
  },
};

// Theme manager class
export class ThemeManager {
  private static readonly STORAGE_KEY_UI = 'chess-ui-theme';
  private static readonly STORAGE_KEY_BOARD = 'chess-board-theme';
  private static readonly DEFAULT_UI_THEME: UITheme = 'light';
  private static readonly DEFAULT_BOARD_THEME: BoardTheme = 'classic';

  private uiTheme: UITheme;
  private boardTheme: BoardTheme;

  constructor() {
    // Load saved themes from localStorage or use defaults
    this.uiTheme = this.loadUITheme();
    this.boardTheme = this.loadBoardTheme();
    
    // Apply themes on initialization
    this.applyUITheme();
    this.applyBoardTheme();
  }

  /**
   * Get current UI theme
   */
  getUITheme(): UITheme {
    return this.uiTheme;
  }

  /**
   * Get current board theme
   */
  getBoardTheme(): BoardTheme {
    return this.boardTheme;
  }

  /**
   * Get theme configuration object
   */
  getThemeConfig(): ThemeConfig {
    return {
      uiTheme: this.uiTheme,
      boardTheme: this.boardTheme,
    };
  }

  /**
   * Set UI theme (light/dark)
   */
  setUITheme(theme: UITheme): void {
    this.uiTheme = theme;
    this.saveUITheme();
    this.applyUITheme();
  }

  /**
   * Set board theme (color scheme)
   */
  setBoardTheme(theme: BoardTheme): void {
    this.boardTheme = theme;
    this.saveBoardTheme();
    this.applyBoardTheme();
  }

  /**
   * Get board colors for current theme
   */
  getBoardColors(): BoardColors {
    return BOARD_THEMES[this.boardTheme];
  }

  /**
   * Load UI theme from localStorage
   */
  private loadUITheme(): UITheme {
    try {
      const saved = localStorage.getItem(ThemeManager.STORAGE_KEY_UI);
      if (saved === 'light' || saved === 'dark') {
        return saved;
      }
    } catch (error) {
      console.warn('Failed to load UI theme from localStorage:', error);
    }
    return ThemeManager.DEFAULT_UI_THEME;
  }

  /**
   * Load board theme from localStorage
   */
  private loadBoardTheme(): BoardTheme {
    try {
      const saved = localStorage.getItem(ThemeManager.STORAGE_KEY_BOARD);
      if (saved && saved in BOARD_THEMES) {
        return saved as BoardTheme;
      }
    } catch (error) {
      console.warn('Failed to load board theme from localStorage:', error);
    }
    return ThemeManager.DEFAULT_BOARD_THEME;
  }

  /**
   * Save UI theme to localStorage
   */
  private saveUITheme(): void {
    try {
      localStorage.setItem(ThemeManager.STORAGE_KEY_UI, this.uiTheme);
    } catch (error) {
      console.warn('Failed to save UI theme to localStorage:', error);
    }
  }

  /**
   * Save board theme to localStorage
   */
  private saveBoardTheme(): void {
    try {
      localStorage.setItem(ThemeManager.STORAGE_KEY_BOARD, this.boardTheme);
    } catch (error) {
      console.warn('Failed to save board theme to localStorage:', error);
    }
  }

  /**
   * Apply UI theme by setting CSS custom properties on document root
   */
  private applyUITheme(): void {
    const root = document.documentElement;
    const colors = UI_THEME_COLORS[this.uiTheme];
    
    Object.entries(colors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Add/remove dark mode class for any CSS that needs it
    if (this.uiTheme === 'dark') {
      root.classList.add('dark-mode');
    } else {
      root.classList.remove('dark-mode');
    }
  }

  /**
   * Apply board theme by setting CSS custom properties
   */
  private applyBoardTheme(): void {
    const root = document.documentElement;
    const colors = this.getBoardColors();
    
    root.style.setProperty('--board-light-square', colors.lightSquare);
    root.style.setProperty('--board-dark-square', colors.darkSquare);
    root.style.setProperty('--board-light-highlight', colors.lightSquareHighlight);
    root.style.setProperty('--board-dark-highlight', colors.darkSquareHighlight);
  }

  /**
   * Toggle between light and dark UI themes
   */
  toggleUITheme(): void {
    const newTheme = this.uiTheme === 'light' ? 'dark' : 'light';
    this.setUITheme(newTheme);
  }
}

// Singleton instance
export const themeManager = new ThemeManager();
